//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useCallback, useEffect, useMemo } from 'react';
import PolarisIcon from '@/components/_common/PolarisIcon';
import {
  selectHasNoNotificationsEnabled,
  selectNotificationSettings,
  selectUnseenNotificationStats,
  selectNotificationTab,
  selectNotificationPaginations
} from '@/redux/selectors/notificationSelectors';
import { changeNotificationSeenState, NotificationVisibility, setNotificationTab } from '@/redux/slices/notificationSlice';
import styled from '@emotion/styled';
import { Button, Grid, IconButton, Tab, Tabs, Tooltip, Typography, lighten } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { uniq } from 'lodash';
import NotificationList from './NotificationList';

interface NotificationsPopoutContentProps {
  showAll?: boolean;
  onShowNotificationSettings: () => void;
  onCloseClicked: () => void;
  onViewAllClicked?: () => void;
}

const NotificationPopoutContent = ({ showAll, onCloseClicked, onViewAllClicked, onShowNotificationSettings }: NotificationsPopoutContentProps) => {
  const notificationSettings = useAppSelector(selectNotificationSettings);
  const tab = useAppSelector(selectNotificationTab);
  const visibility = showAll === true ? NotificationVisibility.ALL : NotificationVisibility.UNSEEN;
  const notificationsPagination = useAppSelector(selectNotificationPaginations(visibility));
  const hasNoSettingsEnabled = useAppSelector(selectHasNoNotificationsEnabled);
  const unseenNotificationStats = useAppSelector(selectUnseenNotificationStats);
  const dispatch = useAppDispatch();

  const hasNotifications = useMemo(() => {
    return (
      Object.keys(notificationsPagination).length > 0 &&
      Object.keys(notificationsPagination).reduce((sum, key) => {
        return sum + (notificationsPagination[key]?.total || 0);
      }, 0) > 0
    );
  }, [notificationsPagination, visibility]);

  const uniqueGroupTypes = useMemo(() => {
    const types = Object.values(notificationSettings?.in_app || {}).map((setting) => setting.group_type);

    return uniq(types);
  }, [notificationSettings]);

  const [newMessagesCount, totalNewMessages] = useMemo(() => {
    const total = Object.values(unseenNotificationStats.group_types).reduce((prev, next) => prev + next, 0);
    const _newMessagesCount: Record<string, number> = {};
    Object.keys(unseenNotificationStats.group_types)
      .filter((type) => unseenNotificationStats.group_types[type] > 0)
      .forEach((type) => {
        _newMessagesCount[type] = unseenNotificationStats.group_types[type];
      });
    return [_newMessagesCount, total];
  }, [unseenNotificationStats]);

  const checkTabFromNewMessages = useCallback(() => {
    const newMessagesTabs = Object.keys(newMessagesCount);
    if (newMessagesTabs.length) {
      return uniqueGroupTypes.find((type) => newMessagesTabs.includes(type));
    }
  }, [uniqueGroupTypes, newMessagesCount]);

  useEffect(() => {
    const selectTab = checkTabFromNewMessages();
    if (selectTab) {
      onTabChange(selectTab);
    }
  }, []);

  useEffect(() => {
    if (!tab) {
      let _tab = checkTabFromNewMessages();
      if (!_tab) {
        _tab = Object.values(notificationSettings?.in_app || {})[0]?.group_type;
      }
      onTabChange(_tab);
    }
  }, [tab, notificationSettings, uniqueGroupTypes, newMessagesCount]);

  const onMarkAllRead = () => {
    dispatch(changeNotificationSeenState({ mark_all_seen: true, showAll }));
  };

  const onTabChange = (newTab: string) => {
    dispatch(setNotificationTab(newTab));
  };

  const getTabLabel = (group_type: string = 'tasks') => {
    switch (group_type?.toLowerCase()) {
      case 'tasks':
        return 'My Tasks';
      case 'jobs':
        return 'Jobs';
      case 'related_tasks':
        return 'Related Tasks';
      case 'processing_exceptions':
        return 'My Processing Exceptions';
      case 'reports':
        return 'My Reports';
      default: {
        const setting = Object.values(notificationSettings?.in_app || {}).find((setting) => setting.group_type === group_type);

        return setting?.label;
      }
    }
  };

  if (hasNoSettingsEnabled) {
    return (
      <ContentWrapper className="notification-popout-content">
        <Grid alignItems="start" justifyContent="end" container sx={{ pr: 1, pt: 1, mb: 0 }}>
          <StyledCloseIconButton onClick={onCloseClicked} size="small">
            <PolarisIcon icon="close" height={20} />
          </StyledCloseIconButton>
        </Grid>
        <NoSettingsEnabled>
          <span>Notifications are disabled</span>
          <Button onClick={onShowNotificationSettings} variant="contained" color="primary" sx={{ mt: 1 }}>
            Edit Notification Settings
          </Button>
        </NoSettingsEnabled>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper className="notification-popout-content">
      <Grid alignItems="start" justifyContent="end" container sx={{ pr: 1, pt: 0, mb: 0 }}>
        <StyledCloseIconButton onClick={onCloseClicked} size="small">
          <PolarisIcon icon="close" height={20} width={20} />
        </StyledCloseIconButton>
      </Grid>
      <Grid alignItems="center" justifyContent="space-between" container sx={{ pl: 2, pr: 1, mb: 1.5 }}>
        <Typography variant="h3" fontSize="1.3rem" fontWeight="normal">
          Notifications
        </Typography>
        {totalNewMessages > 0 && (
          <Button onClick={onMarkAllRead} variant="text" color="default" sx={{ py: 0 }} startIcon={<PolarisIcon icon="check_double" height={18} />}>
            Mark all as read
          </Button>
        )}
      </Grid>
      <Grid justifyContent="space-between" container sx={{ pl: 1.75 }}>
        <StyledTabs indicatorColor="secondary" value={tab || 'TASKS'} onChange={(_e, v) => onTabChange(v)}>
          {hasNotifications &&
            uniqueGroupTypes.map((group_type) => (
              <StyledTab
                key={group_type}
                value={group_type}
                label={
                  <LabelContainer>
                    <span>{getTabLabel(group_type)}</span>
                    {!!newMessagesCount[group_type] && <MessageCount>{newMessagesCount[group_type]?.toLocaleString()}</MessageCount>}
                  </LabelContainer>
                }
              />
            ))}
        </StyledTabs>
        <Tooltip title="Notification Settings" arrow placement="left">
          <IconButton onClick={onShowNotificationSettings} size="small" style={{ marginRight: '12px', opacity: '0.6' }}>
            <PolarisIcon icon="settings" />
          </IconButton>
        </Tooltip>
      </Grid>
      {!!tab && <NotificationList showAll={showAll} onClose={onCloseClicked} type={tab} />}
      {onViewAllClicked ? (
        <Grid container justifyContent="flex-end" sx={{ p: 1, mb: 1.5 }}>
          <Button onClick={onViewAllClicked} size="small" variant="contained">
            View All Notifications
          </Button>
        </Grid>
      ) : (
        <></>
      )}
    </ContentWrapper>
  );
};

export default NotificationPopoutContent;

const ContentWrapper = styled.div`
  width: 433px;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  overflow: hidden;
  padding-top: ${(props) => props.theme.spacing(1)};
  background-color: ${({ theme }) => theme.palette.background.paper};
  box-shadow: ${({ theme }) => theme.shadows[10]};
  .MuiModal-root & {
    box-shadow: none;
  }
`;

const StyledCloseIconButton = styled(IconButton)`
  .MuiIconButton-root {
    margin-top: ${({ theme }) => theme.spacing(-1)};
    margin-left: ${({ theme }) => theme.spacing(-2)};
    opacity: 0.5;
    transition: ease opacity 250ms;
  }

  &:hover {
    opacity: 1;
    transition: ease opacity 250ms;
  }
`;

const StyledTabs = styled(Tabs)`
  min-height: 28px;
`;

const StyledTab = styled(Tab)`
  text-transform: capitalize;
  padding: 0 3px;
  min-width: 0;
  min-height: 20px;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  margin-right: ${({ theme }) => theme.spacing(1)};
  &.Mui-selected {
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
`;

const MessageCount = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 1px;
  background-color: ${({ theme }) => lighten(theme.palette.background.paper, 0.2)};
  margin-left: ${({ theme }) => theme.spacing(1)};
  min-width: 20px;
  height: 20px;
  padding: ${(props) => props.theme.spacing(0, 0.5)};
  text-align: center;
  font-size: 0.7rem;
  line-height: 0.7rem;
  font-style: normal;
  font-weight: 700;
`;

const NoSettingsEnabled = styled.div`
  padding: ${({ theme }) => theme.spacing(1, 0, 5, 0)};
  text-align: center;
  font-style: italic;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  span {
    font-style: italic;
    line-height: normal;
    opacity: 0.7;
  }
`;
